import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BankModal from './BankModal';
import Pagination from '../../common/Pagination';
import LoadingOverlay from '../../common/LoadingOverlay';

function BankIndex() {
  // Session validation
  useSessionValidation();
  const { canCreateMaster, canEditMaster, canDeleteMaster } = usePermissions();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedBank, setSelectedBank] = useState(null);

  // Dropdown data states
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return; // Prevent calls without required data

    setLoading(true);
    Promise.all([
      fetchtableData(),
      fetchbalanceSheetData()
    ]).finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  const fetchtableData = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      console.log('Bank data response:', response);

      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        console.log('Bank data loaded successfully');
      } else {
        console.log('Bank API failed or returned no data');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Bank Details:', error);
      setData([]);
    }
  };

  const fetchbalanceSheetData = async () => {
    try {
      // Fetch Balance Sheet Heads with better error handling
      const balanceSheetResponse = await fetchData('BalanceSheetHeadApi', `/${companyId}`, token);
      console.log('Balance Sheet Response:', balanceSheetResponse);

      if (balanceSheetResponse && balanceSheetResponse.responseCode === '1111' && balanceSheetResponse.response_data) {
        setBalanceSheetHeads(balanceSheetResponse.response_data);
        console.log('Balance Sheet Heads loaded successfully');
      } else {
        console.log('Balance Sheet API failed or returned no data');
        setBalanceSheetHeads([]);
      }

    } catch (error) {
      console.error('Error fetching balance sheet data:', error);
      setBalanceSheetHeads([]);
    }
  };

  const handleDelete = async () => {
    deleteData('BankApi', `${deleteItemId}`, token)
      .then(() => {
        fetchtableData(); // Refresh data after deletion
        setDeleteItemId(null); // Reset the item ID
        setConfirmDialogOpen(false); // Close the dialog
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const handleActivate = (itemId) => {
    activateData('BankApi', `${itemId}`, token)
      .then(() => {
        fetchtableData(); // Refresh data after activation
      })
      .catch((error) => {
        console.error('Error activating record:', error);
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  // Modal handlers
  const handleOpenModal = (mode, bank = null) => {
    setModalMode(mode);
    setSelectedBank(bank);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBank(null);
    window.location.reload();
  };

  const handleSaveBank = () => {
    fetchtableData(); // Refresh data
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter data based on search term
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.Name?.toLowerCase().includes(searchLower) ||
      item.AccountNumber?.toLowerCase().includes(searchLower) ||
      item.IFSCCode?.toLowerCase().includes(searchLower) ||
      item.BranchName?.toLowerCase().includes(searchLower) ||
      item.ContactNumber?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Define table columns
  const columns = [
    { field: 'Name', header: 'Bank Name' },
    { field: 'AccountNumber', header: 'Account Number' },
    { field: 'IFSCCode', header: 'IFSC Code' },
    { field: 'BranchName', header: 'Branch Name' },
    { field: 'ContactNumber', header: 'Contact Number' },
    { field: 'IsPrimary', header: 'Primary' },
  ];

  // Define action buttons for each row
  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          👁️
        </Button>
      </Tooltip>

      {canEditMaster ? (
        <Tooltip title="Edit Bank" arrow>
          <Button size="small" onClick={() => handleOpenModal('edit', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)', border: 'none', fontSize: '10px', fontWeight: '600' }}>✏️</Button>
        </Tooltip>
      ) : (
        <Tooltip title="No edit permission" arrow>
          <span><Button size="small" disabled style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', fontSize: '10px', opacity: 0.4 }}>✏️</Button></span>
        </Tooltip>
      )}

      {canDeleteMaster ? (
        <Tooltip title={item.IsActive ? "Delete Bank" : "Activate Bank"} arrow>
          <Button size="small" onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', boxShadow: item.IsActive ? '0 1px 4px rgba(255, 107, 107, 0.3)' : '0 1px 4px rgba(78, 205, 196, 0.3)', border: 'none', fontSize: '10px', fontWeight: '600' }}>{item.IsActive ? '🗑️' : '✅'}</Button>
        </Tooltip>
      ) : (
        <Tooltip title="No delete permission" arrow>
          <span><Button size="small" disabled style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', fontSize: '10px', opacity: 0.4 }}>{item.IsActive ? '🗑️' : '✅'}</Button></span>
        </Tooltip>
      )}
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        {loading && <LoadingOverlay message="Loading..." />}

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Bank</span>
                </h4>

              </div>
            </div>
            {canCreateMaster && (
              <Tooltip title="Create a new bank" arrow>
                <Button
                  variant="contained"
                  onClick={() => handleOpenModal('create')}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                    border: 'none',
                    minHeight: '26px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '12px' }}>+</span>
                  Create Bank
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Search Bar */}
          <div style={{ marginBottom: '12px' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, account number, IFSC code, branch name, or contact..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#0f3460', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '13px',
                  '&:hover fieldset': {
                    borderColor: '#0f3460',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0f3460',
                  },
                },
              }}
            />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="bank table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '8px 12px',
                        textAlign: column.field === 'IsPrimary' ? 'center' : 'left'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '8px 12px',
                      textAlign: 'right'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.Name}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.AccountNumber}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.IFSCCode}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.BranchName}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.ContactNumber}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>
                        {item.IsPrimary ? '✅' : '❌'}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this bank record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bank Modal */}
        <BankModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveBank}
          bankData={selectedBank}
          mode={modalMode}
          balanceSheetHeads={balanceSheetHeads}
          existingBanks={data}
        />

      </div>
    </Fade >
  );
}

export default BankIndex;